import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Container, Flex, Heading, Section, Text } from '@radix-ui/themes';
import { useSelector } from 'react-redux';
import { selectDevice } from 'store/slices/common';

import Visual from 'components/common/visual';
import Breadcrumbs from 'components/common/breadcrumbs';
import PageTitle from 'components/layout/pageTitle';

import './style.scss';

const SLOT_CYCLES = 1;
const SLOT_DURATION = '1.6s';
const SLOT_EASING = 'cubic-bezier(0.16, 1, 0.3, 1)';
const SLOT_STAGGER = 40;
const THRESHOLD_GOAL = 0.2;
const THRESHOLD_BUSINESS_MOBILE = 0.2;
const THRESHOLD_BUSINESS_DESKTOP = 0.5;
const THRESHOLD_YEAR = 0.4;

// --- 커스텀 훅 ---
function useReducedMotion() {
  const [reduced, setReduced] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );

  useEffect(() => {
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handler = e => setReduced(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  return reduced;
}

function useScrollTopReset() {
  const [resetKey, setResetKey] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY === 0) {
        setResetKey(prev => prev + 1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return resetKey;
}

function useIntersectionVisible(ref, threshold = 0.3, resetKey = 0) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, threshold, resetKey]);

  return visible;
}

function useScrollThenIntersect(ref, threshold = 0.3, resetKey = 0) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    setVisible(false);
    let observer;

    const handleScroll = () => {
      window.removeEventListener('scroll', handleScroll);

      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.disconnect();
          }
        },
        { threshold }
      );

      if (ref.current) observer.observe(ref.current);
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (observer) observer.disconnect();
    };
  }, [ref, threshold, resetKey]);

  return visible;
}

// --- 슬롯 모션 컴포넌트 ---
function SlotDigit({ digit, start, delay = 0 }) {
  const [isAnimating, setIsAnimating] = useState(false);
  const reducedMotion = useReducedMotion();
  const totalSteps = reducedMotion ? digit : SLOT_CYCLES * 10 + digit;

  useEffect(() => {
    if (!start) {
      setIsAnimating(false);
      return;
    }
    const timer = setTimeout(() => setIsAnimating(true), delay);
    return () => clearTimeout(timer);
  }, [start, delay]);

  const active = isAnimating || (start && reducedMotion);

  return (
    <span className="slot-digit-wrap">
      <span
        className="slot-digit-strip"
        style={{
          transform: active ? `translateY(-${totalSteps}em)` : 'translateY(0)',
          transition: active && !reducedMotion ? `transform ${SLOT_DURATION} ${SLOT_EASING}` : 'none'
        }}
      >
        {Array.from({ length: totalSteps + 1 }, (_, i) => (
          <span key={i} className="slot-digit-item">
            {i % 10}
          </span>
        ))}
      </span>
    </span>
  );
}

function SlotNumber({ end, start, delay = 0 }) {
  const formatted = useMemo(() => end.toLocaleString(), [end]);

  return formatted
    .split('')
    .map((char, i) =>
      char === ',' ? (
        <span key={`comma-${i}`}>,</span>
      ) : (
        <SlotDigit key={`digit-${i}`} digit={parseInt(char)} start={start} delay={delay + i * SLOT_STAGGER} />
      )
    );
}

// --- 소개 > 사업 소개 페이지 ---
export default function Intro() {
  const deviceType = useSelector(selectDevice);
  const isMobile = deviceType === 'mobile';
  const resetKey = useScrollTopReset();

  const goalRef = useRef(null);
  const businessRef = useRef(null);
  const year2023Ref = useRef(null);
  const year2024Ref = useRef(null);
  const year2025Ref = useRef(null);

  const isGoalVisible = useScrollThenIntersect(goalRef, THRESHOLD_GOAL, resetKey);
  const isBusinessVisible = useIntersectionVisible(
    businessRef,
    isMobile ? THRESHOLD_BUSINESS_MOBILE : THRESHOLD_BUSINESS_DESKTOP,
    resetKey
  );
  const isYear2023Visible = useIntersectionVisible(year2023Ref, THRESHOLD_YEAR, resetKey);
  const isYear2024Visible = useIntersectionVisible(year2024Ref, THRESHOLD_YEAR, resetKey);
  const isYear2025Visible = useIntersectionVisible(year2025Ref, THRESHOLD_YEAR, resetKey);

  return (
    <>
      <Visual category="introduction" title="소개" />
      <Breadcrumbs category="introduction" />
      <Section p="0" pb={isMobile ? 'var(--space-18)' : 'var(--space-35)'} className="introduction-wrap">
        <Container size={isMobile ? '1' : '4'}>
          <PageTitle icon="school" title="사업소개" />

          {/* 사업목적 */}
          <Box className="intro-goal" mb={isMobile ? 'var(--space-9)' : 'var(--space-20)'}>
            <Heading as="h4" className={isMobile ? 'sub-title' : 'title4'}>
              사업목적
            </Heading>
            <Flex align={'center'} justify={'between'} gap={'var(--space-6)'} className="goal-content">
              <Box className="goal-text" ref={goalRef}>
                <img src={require('assets/images/logo/logo.svg').default} alt="SW미래채움" className="goal-logo" />
                <Text as="p" className="goal-desc">
                  AI‧SW 교육 인프라가 부족한 지역 대상으로 <br /> SW미래채움센터 운영을 통하여 <br />
                  <Text as="span" className={`highlight${isGoalVisible ? ' active' : ''}`}>
                    초·중등학생의 AI·SW교육 기획 확대 및 교육격차 해소
                  </Text>
                </Text>
              </Box>
              <Box className="goal-illust">
                <img src={require(`assets/images/pages/intro/intro-illust${isMobile ? '-mo' : ''}.png`)} alt="" />
              </Box>
            </Flex>
          </Box>

          {/* 사업내용 */}
          <Box mb={isMobile ? 'var(--space-9)' : 'var(--space-25)'} className="business-wrap">
            <Heading as="h4" className={isMobile ? 'sub-title' : 'title4'}>
              사업내용
            </Heading>
            <Flex
              align={'stretch'}
              gap={'var(--space-7)'}
              className={`business-list${isBusinessVisible ? ' active' : ''}`}
              ref={businessRef}
            >
              <Box className="business-card">
                <Box className="business-img school">
                  <img src={require(`assets/images/pages/intro/intro-school${isMobile ? '-mo' : ''}.png`)} alt="" />
                </Box>
                <Box className="business-info yellow">
                  <Heading as="h5">SW미래채움센터 운영</Heading>
                  <span className="divider" />
                  <Text as="p">AI, VR‧AR, 드론 등 최신 기술에 대한 체험장비, 상시 체험‧교육 가능한 AI‧SW교육거점 구축‧운영</Text>
                </Box>
              </Box>

              <Box className="business-card">
                <Box className="business-img teacher">
                  <img src={require(`assets/images/pages/intro/intro-teacher${isMobile ? '-mo' : ''}.png`)} alt="" />
                </Box>
                <Box className="business-info green">
                  <Heading as="h5">강사 양성·위촉</Heading>
                  <span className="divider" />
                  <Text as="p">
                    지역 內 미취업청년, 경력보유여성, 퇴직자 등을 대상으로 체계적인 강사 육성 프로그램을 적용하여 양성 및 자립 지원
                  </Text>
                </Box>
              </Box>

              <Box className="business-card">
                <Box className="business-img edu">
                  <img src={require(`assets/images/pages/intro/intro-edu${isMobile ? '-mo' : ''}.png`)} alt="" />
                </Box>
                <Box className="business-info blue">
                  <Heading as="h5">교육프로그램 운영</Heading>
                  <span className="divider" />
                  <Text as="p">AI‧SW교육프로그램을 개발하여 교육기회가 부족한 초‧중등학생 대상 찾아가는 방문교육 추진</Text>
                </Box>
              </Box>
            </Flex>
          </Box>

          {/* 주요성과 */}
          <Box className="performance-wrap">
            <Heading as="h4" className={isMobile ? 'sub-title' : 'title4'}>
              주요성과(최근 3개년)
            </Heading>
            {isMobile ? (
              <Flex direction={'column'} className="mo-timeline">
                <Box className="tl-section yellow" ref={year2023Ref}>
                  <img src={require('assets/images/pages/intro/road-map-yellow.png')} alt="" loading="lazy" />
                  <Flex direction={'column'} className="tl-content">
                    <Text as="p" className="tl-year">
                      2023년
                    </Text>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-teacher-yellow.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            강사양성
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={1181} start={isYear2023Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-school-yellow.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            교육운영
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={253742} start={isYear2023Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'flex-start'} gap={'var(--space-3)'} className="tl-card-row">
                      <span className="tl-dot" />
                      <Box className="tl-card">
                        <Heading as="h5">2023년 주요 추진사항</Heading>
                        <ul>
                          <li>SW미래채움 2개 센터 추가 구축(광주, 전북)</li>
                          <li>SW미래채움×늘봄학교 협력강화</li>
                        </ul>
                      </Box>
                    </Flex>
                  </Flex>
                </Box>

                <Box className="tl-section green" ref={year2024Ref}>
                  <img src={require('assets/images/pages/intro/road-map-green.png')} alt="" loading="lazy" />
                  <Flex direction={'column'} className="tl-content">
                    <Text as="p" className="tl-year">
                      2024년
                    </Text>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-teacher-green.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            강사양성
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={1317} start={isYear2024Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-school-green.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            교육운영
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={302782} start={isYear2024Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'flex-start'} gap={'var(--space-3)'} className="tl-card-row">
                      <span className="tl-dot" />
                      <Box className="tl-card">
                        <Heading as="h5">2024년 주요 추진사항</Heading>
                        <ul>
                          <li>SW미래채움 13개 센터 운영</li>
                          <li>우수 고등학생 대상 해외역량강화 교육 추진</li>
                        </ul>
                      </Box>
                    </Flex>
                  </Flex>
                </Box>

                <Box className="tl-section blue" ref={year2025Ref}>
                  <img src={require('assets/images/pages/intro/road-map-blue.png')} alt="" loading="lazy" />
                  <Flex direction={'column'} className="tl-content">
                    <Text as="p" className="tl-year">
                      2025년
                    </Text>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-teacher-blue.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            강사양성
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={1304} start={isYear2025Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'center'} gap={'var(--space-3)'} className="tl-stat">
                      <span className="tl-dot" />
                      <Flex align={'center'} gap={'var(--space-4)'}>
                        <img src={require('assets/images/pages/intro/icon-school-blue.svg').default} alt="" className="tl-icon" />
                        <Box className="tl-stat-text">
                          <Text as="p" className="tl-label">
                            교육운영
                          </Text>
                          <Text as="p" className="tl-value">
                            <strong>
                              <SlotNumber end={276015} start={isYear2025Visible} />
                            </strong>
                            <span>명</span>
                          </Text>
                        </Box>
                      </Flex>
                    </Flex>
                    <Flex align={'flex-start'} gap={'var(--space-3)'} className="tl-card-row">
                      <span className="tl-dot" />
                      <Box className="tl-card">
                        <Heading as="h5">2025년 주요 추진사항</Heading>
                        <ul>
                          <li>SW미래채움×네이버커넥트재단 협력</li>
                          <li>수석강사제 및 강사연구회 발족</li>
                          <li>우수 강사 대상 해외역량강화교육 추진</li>
                        </ul>
                      </Box>
                    </Flex>
                  </Flex>
                </Box>
              </Flex>
            ) : (
              <Box className="road-map">
                <img src={require('assets/images/pages/intro/road-map.svg').default} alt="" className="road-bg" />

                <Text as="p" className="year-text y-2023">
                  2023년
                </Text>
                <img src={require('assets/images/pages/intro/arrow-yellow.png')} alt="" className="year-arrow arrow-2023" />

                <Flex direction={'column'} align={'center'} className="stat-item s-2023-teacher" ref={year2023Ref}>
                  <img src={require('assets/images/pages/intro/icon-teacher-yellow.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot yellow" />
                  <Text as="p" className="stat-label">
                    강사양성
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={1181} start={isYear2023Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Flex direction={'column'} align={'center'} className="stat-item s-2023-school">
                  <img src={require('assets/images/pages/intro/icon-school-yellow.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot yellow" />
                  <Text as="p" className="stat-label">
                    교육운영
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={253742} start={isYear2023Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Box className="info-card card-2023 yellow">
                  <span className="card-deco" />
                  <Box className="card-body">
                    <Heading as="h5">2023년 주요 추진사항</Heading>
                    <ul>
                      <li>SW미래채움 2개 센터 추가 구축(광주, 전북)</li>
                      <li>SW미래채움×늘봄학교 협력강화</li>
                    </ul>
                  </Box>
                </Box>

                <Text as="p" className="year-text y-2024">
                  2024년
                </Text>

                <Flex direction={'column'} align={'center'} className="stat-item s-2024-school" ref={year2024Ref}>
                  <img src={require('assets/images/pages/intro/icon-school-green.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot green" />
                  <Text as="p" className="stat-label">
                    교육운영
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={302782} start={isYear2024Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Flex direction={'column'} align={'center'} className="stat-item s-2024-teacher">
                  <img src={require('assets/images/pages/intro/icon-teacher-green.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot green" />
                  <Text as="p" className="stat-label">
                    강사양성
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={1317} start={isYear2024Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Box className="info-card card-2024 green">
                  <span className="card-deco" />
                  <Box className="card-body">
                    <Heading as="h5">2024년 주요 추진사항</Heading>
                    <ul>
                      <li>SW미래채움 13개 센터 운영</li>
                      <li>우수 고등학생 대상 해외역량강화 교육 추진</li>
                    </ul>
                  </Box>
                </Box>

                <Text as="p" className="year-text y-2025">
                  2025년
                </Text>

                <Flex direction={'column'} align={'center'} className="stat-item s-2025-teacher" ref={year2025Ref}>
                  <img src={require('assets/images/pages/intro/icon-teacher-blue.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot blue" />
                  <Text as="p" className="stat-label">
                    강사양성
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={1304} start={isYear2025Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Flex direction={'column'} align={'center'} className="stat-item s-2025-school">
                  <img src={require('assets/images/pages/intro/icon-school-blue.svg').default} alt="" className="stat-icon" />
                  <span className="stat-dot blue" />
                  <Text as="p" className="stat-label">
                    교육운영
                  </Text>
                  <Text as="p" className="stat-value">
                    <strong>
                      <SlotNumber end={276015} start={isYear2025Visible} />
                    </strong>
                    <span>명</span>
                  </Text>
                </Flex>

                <Box className="info-card card-2025 blue">
                  <span className="card-deco" />
                  <Box className="card-body">
                    <Heading as="h5">2025년 주요 추진사항</Heading>
                    <ul>
                      <li>SW미래채움×네이버커넥트재단 협력</li>
                      <li>수석강사제 및 강사연구회 발족</li>
                      <li>우수 강사 대상 해외역량강화교육 추진</li>
                    </ul>
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Container>
      </Section>
    </>
  );
}
